import { ApiResponse } from '../../utils/ApiService';
import apiClient from '../client';
import { ServerTreatmentPlanStep } from '../types/treatmentPlanStep';
export enum StepStatus {
  PENDING = 'PENDING',
  IN_PROGRESS = 'IN_PROGRESS',
  COMPLETED = 'COMPLETED',
  OVER_DUE = 'OVER_DUE',
  PAUSED = 'PAUSED',
  ARCHIVED = 'ARCHIVED',
  FLAGGED = 'FLAGGED',
}

export enum TimeSpecificityType {
  START_TODAY = 'START_TODAY',
  BEFORE_AFTER_PERIOD = 'BEFORE_AFTER_PERIOD',
  DATE_RANGE = 'DATE_RANGE',
  BEFORE_AFTER_TASK = 'BEFORE_AFTER_TASK',
}

export interface Media {
  id: string;
  title: string;
  file: string;
  mediaType: 'AUDIO' | 'VIDEO' | 'IMAGE' | 'DOC';
}

export interface CreateStepDto {
  title: string;
  description?: string;
  startDate?: string;
  endDate?: string;
  audioId?: string;
  mediaId?: string;
  treatmentId?: string;
  stepNum?: number;
  note?: string;
  folderId?: string;
  doctorId: string;
  tags?: string[];
  status?: StepStatus;
  flagIfMissed?: boolean;
  timeSpecificity?: TimeSpecificityType;
  relativeToStepId?: string;
  relativeToDate?: string;
  offsetDays?: number;
}

export type StepResponse = {
  id: number;
  name: string;
  type: string;
  uses: number;
  date_created: string;
  description: string;
};

export interface StepsHierarchy {
  id: number;
  name: string;
  type: string;
  date_created: string;
  children: StepResponse[];
}

export interface Step {
  id?: number;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  step_num: number | null;
  folder: number | null;
  treatment: number | null;
  note: string;
  complete: boolean;
  audio: string | null;
  media: string | null;
  base_step: number | null;
  tags: string[];
}

export interface CreateFolderRequest {
  name: string;
  parent_id: number | null;
}

export interface Folder {
  id: number;
  name: string;
  parent_id: number | null;
}

// New interfaces for Treatment Plans
export interface TreatmentPlanStep {
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  audio: string | null;
  media: string | null;
  step_num: number;
  folder: number | null;
  note: string;
  complete?: boolean;
  tags?: string[];
}
export interface TreatmentPlanStepResponse extends TreatmentPlanStep {
  id: number;
}
export interface CreateTreatmentPlanRequest {
  name: string;
  description: string;
  tags: string[];
  assign_patients: number[];
  folder?: number;
  steps: ServerTreatmentPlanStep[];
  is_template: boolean;
}

export interface DoctorInfo {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  title: string;
}

export interface PatientUser {
  id: number;
  email: string;
  email_verified: boolean;
  user_status: string;
}

export interface PatientInfo {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  user?: PatientUser;
}

export interface TreatmentPlanResponse {
  id: number | null;
  name: string;
  description: string;
  steps: ServerTreatmentPlanStep[];
  tags?: string[];
  last_active?: string | null;
  is_draft: boolean;
  status?: string;
  folder?: number | null;
  patient: number | null;
  doctor: number;
  base_treatment?: number | null;
  created_at: string;
  updated_at: string;
  created_by: string;
  updated_by: string;
  doctor_info?: DoctorInfo;
  patient_info?: PatientInfo;
}

export interface DetailedTreatmentPlanStep {
  id: number;
  tags: string[];
  media: string | null;
  title: string;
  description: string;
  step_num: number;
  note: string;
  complete: boolean;
  completed_at: string | null;
  status: StepStatus;
  flag_if_missed: boolean;
  start_date: string | null;
  end_date: string | null;
  time_specificity: TimeSpecificityType | null;
  relative_to_date: string | null;
  offset_amount: number;
  offset_units: string | null;
  relative_direction: string | null;
  urgency: string | null;
  audio: string | null;
  treatment: number;
  folder: number | null;
  base_step: number | null;
  doctor: number;
  relative_to_step: number | null;
}

export interface DetailedTreatmentPlanResponse {
  id: number;
  steps: DetailedTreatmentPlanStep[];
  tags: string[];
  name: string;
  description: string;
  last_active: string | null;
  folder: number | null;
  patient: number | null;
  doctor: number;
  base_treatment: number | null;
  assigned_patients: number[];
}

export interface TreatmentPlanFolder {
  id: number;
  name: string;
  type: string;
  date_created: string;
  children: (TreatmentPlanResponse | TreatmentPlanFolder)[];
}

export interface UpdateTreatmentPlanRequest {
  name?: string;
  description?: string;
  tags?: string[];
  assign_patients?: number[];
  steps?: ServerTreatmentPlanStep[];
  status?: 'ACTIVE' | 'COMPLETE';
  folder?: number;
}

export interface UpdateStepRequest {
  title?: string;
  description?: string;
  start_date?: string;
  end_date?: string;
  note?: string;
  complete?: boolean;
}

export type DeliveryMethod = 'email' | 'phone' | 'both';

export interface SendNotificationRequest {
  patient_ids: number[];
  delivery_method: DeliveryMethod;
  doctor_note?: string;
}

export const TreatmentPlanAPI = {
  createReusableStep: async (data: CreateStepDto): Promise<ApiResponse<Step>> => {
    const response = await apiClient.post('/steps/', data);
    return {
      success: true,
      data: response.data,
    };
  },
  getReusableSteps: async (): Promise<ApiResponse<StepsHierarchy>> => {
    const response = await apiClient.get('/steps/');
    return {
      success: true,
      data: response.data,
    };
  },
  getStepById: async (id: number): Promise<ApiResponse<ServerTreatmentPlanStep>> => {
    const response = await apiClient.get(`/steps/${id}/`);
    return {
      success: true,
      data: response.data,
    };
  },
  deleteReusableStep: async (id: number): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/steps/${id}/`);
    return {
      success: true,
      data: response.data,
    };
  },
  createFolder: async (data: CreateFolderRequest): Promise<ApiResponse<Folder>> => {
    const response = await apiClient.post('/folders/', data);
    return {
      success: true,
      data: response.data,
    };
  },
  deleteFolder: async (id: number): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/folders/${id}/`);
    return {
      success: true,
      data: response.data,
    };
  },
  getTreatmentPlans: async (): Promise<ApiResponse<TreatmentPlanFolder>> => {
    const response = await apiClient.get('/treatment-plans/');
    return {
      success: true,
      data: response.data,
    };
  },
  getTreatmentPlanById: async (id: number): Promise<ApiResponse<DetailedTreatmentPlanResponse>> => {
    const response = await apiClient.get(`/treatment-plans/${id}/`);
    return {
      success: true,
      data: response.data,
    };
  },
  createTreatmentPlan: async (
    data: CreateTreatmentPlanRequest
  ): Promise<ApiResponse<TreatmentPlanResponse[] | TreatmentPlanResponse>> => {
    const response = await apiClient.post('/treatment-plans/', data);
    return {
      success: true,
      data: response.data,
    };
  },
  updateTreatmentPlan: async (
    data: UpdateTreatmentPlanRequest & { id: number }
  ): Promise<ApiResponse<TreatmentPlanResponse>> => {
    const { id, ...dataWithoutId } = data;
    const response = await apiClient.patch(`/treatment-plans/${id}/`, dataWithoutId);
    return {
      success: true,
      data: response.data,
    };
  },
  deleteTreatmentPlan: async (id: number): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/treatment-plans/${id}/`);
    return {
      success: true,
      data: response.data,
    };
  },
  getTreatmentPlanSteps: async (
    treatmentPlanId: number
  ): Promise<ApiResponse<TreatmentPlanStepResponse[]>> => {
    const response = await apiClient.get(`/treatment-plans/${treatmentPlanId}/steps/`);
    return {
      success: true,
      data: response.data,
    };
  },
  createTreatmentPlanSteps: async (
    treatmentPlanId: number,
    data: TreatmentPlanStep[]
  ): Promise<ApiResponse<TreatmentPlanStepResponse>> => {
    const response = await apiClient.post(`/treatment-plans/${treatmentPlanId}/steps/`, data);
    return {
      success: true,
      data: response.data,
    };
  },
  updateTreatmentPlanStep: async (
    treatmentPlanId: number,
    stepId: number,
    data: UpdateStepRequest
  ): Promise<ApiResponse<TreatmentPlanStepResponse>> => {
    const response = await apiClient.patch(
      `/treatment-plans/${treatmentPlanId}/steps/${stepId}/`,
      data
    );
    return {
      success: true,
      data: response.data,
    };
  },
  deleteTreatmentPlanStep: async (
    treatmentPlanId: number,
    stepId: number
  ): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/treatment-plans/${treatmentPlanId}/steps/${stepId}/`);
    return {
      success: true,
      data: response.data,
    };
  },
  sendNotification: async (
    treatmentPlanId: number,
    data: SendNotificationRequest
  ): Promise<ApiResponse<void>> => {
    const response = await apiClient.post(
      `/treatment-plans/${treatmentPlanId}/send-notification/`,
      data
    );
    return {
      success: true,
      data: response.data,
    };
  },

  // Draft-specific endpoints
  saveDraft: async (data: {
    name: string;
    description?: string;
    patient_id?: number;
    steps: ServerTreatmentPlanStep[];
  }): Promise<ApiResponse<TreatmentPlanResponse>> => {
    const response = await apiClient.post('/treatment-plans/save-draft/', data);
    return {
      success: true,
      data: response.data,
    };
  },

  getDrafts: async (): Promise<ApiResponse<TreatmentPlanResponse[]>> => {
    const response = await apiClient.get('/treatment-plans/drafts/');
    return {
      success: true,
      data: response.data,
    };
  },

  updateDraft: async (
    id: number,
    data: {
      name?: string;
      description?: string;
      steps?: ServerTreatmentPlanStep[];
    }
  ): Promise<ApiResponse<TreatmentPlanResponse>> => {
    const response = await apiClient.patch(`/treatment-plans/${id}/update-draft/`, data);
    return {
      success: true,
      data: response.data,
    };
  },

  publishDraft: async (
    id: number,
    data: {
      assign_patients: number[];
    }
  ): Promise<ApiResponse<TreatmentPlanResponse>> => {
    const response = await apiClient.post(`/treatment-plans/${id}/publish-draft/`, data);
    return {
      success: true,
      data: response.data,
    };
  },

  deleteDraft: async (id: number): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/treatment-plans/${id}/delete-draft/`);
    return {
      success: true,
      data: response.data,
    };
  },

  deleteDrafts: async (ids: number[]): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete('/treatment-plans/delete-drafts/', {
      data: { treatment_plan_ids: ids },
    });
    return {
      success: true,
      data: response.data,
    };
  },
};

/**
 * Transcribe audio file to text
 * @param audioFile Blob or File
 * @returns { transcription: string }
 */
export const transcribeAudioService = async (
  audioFile: Blob
): Promise<ApiResponse<{ job_id: number; status: string }>> => {
  const formData = new FormData();
  formData.append('audio', audioFile, 'recording.m4a');
  const response = await apiClient.post('/api/transcribe/', formData, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return { success: true, data: response.data };
};

export const fetchTranscriptionJob = async (
  jobId: number
): Promise<
  ApiResponse<{
    status: string;
    transcription?: string;
    detected_language?: string;
    error?: string;
  }>
> => {
  const response = await apiClient.get('/api/transcribe/', {
    params: { job_id: jobId },
  });
  return { success: true, data: response.data };
};

/**
 * Send transcript to treatment plan API
 * @param transcript string
 * @param patientId number (optional)
 * @returns { ... }
 */
export const transcribeToPlanService = async (
  transcript: string,
  patientId?: number,
  options?: { break_into_tasks: boolean; add_dates: boolean }
): Promise<ApiResponse<any>> => {
  const response = await apiClient.post('/api/transcribe-to-plan/', {
    transcript,
    ...(patientId && { patient_id: patientId }),
    ...(options || {}),
  });
  return { success: true, data: response.data };
};
